// DNA Pay - Relatórios
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  BarChart3, TrendingUp, TrendingDown, Users, 
  DollarSign, Calendar, Download, Loader2,
  PieChart, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export default function DnaPayRelatorios() {
  const [loading, setLoading] = useState(true);
  const [ano, setAno] = useState(new Date().getFullYear());
  const [receitaMensal, setReceitaMensal] = useState([]);
  const [inadimplenciaMensal, setInadimplenciaMensal] = useState([]);
  const [metodosPagamento, setMetodosPagamento] = useState([]);
  const [relatorioInadimplencia, setRelatorioInadimplencia] = useState(null);
  const [canonical, setCanonical] = useState(null); // V5.2 · fonte canônica db.titles

  useEffect(() => {
    loadData();
  }, [ano]);

  const loadData = async () => {
    setLoading(true);
    try {
      // V5.2 · shadow mode canônico (?engine=legacy desativa)
      const useLegacy = new URLSearchParams(window.location.search).get('engine') === 'legacy';
      const authHeader = { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } };
      const [receitaRes, inadRes, metodosRes, relInadRes, canonicalRes] = await Promise.all([
        fetch(`${API_URL}/api/dnapay/dashboard/grafico/receita-mensal?ano=${ano}`),
        fetch(`${API_URL}/api/dnapay/dashboard/grafico/inadimplencia-mensal?ano=${ano}`),
        fetch(`${API_URL}/api/dnapay/dashboard/grafico/metodos-pagamento?ano=${ano}`),
        fetch(`${API_URL}/api/dnapay/billing/relatorio/inadimplencia`),
        useLegacy
          ? Promise.resolve({ ok: false })
          : fetch(`${API_URL}/api/finance/dashboard?year=${ano}`, authHeader),
      ]);

      if (receitaRes.ok) setReceitaMensal(await receitaRes.json());
      if (inadRes.ok) setInadimplenciaMensal(await inadRes.json());
      if (metodosRes.ok) setMetodosPagamento(await metodosRes.json());
      if (relInadRes.ok) setRelatorioInadimplencia(await relInadRes.json());
      if (canonicalRes.ok) {
        const c = await canonicalRes.json();
        setCanonical(c);
        console.info('[relatorios] motor canônico', c);
      }
    } catch (error) {
      toast.error('Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

  // Cálculos de totais (V5.2 · canônico sobrescreve quando disponível)
  const totalPrevisao = canonical ? (canonical.a_receber + canonical.recebido) : receitaMensal.reduce((acc, m) => acc + (m.previsao || 0), 0);
  const totalRecebido = canonical ? canonical.recebido : receitaMensal.reduce((acc, m) => acc + (m.recebido || 0), 0);
  const totalInadimplente = canonical ? canonical.vencido : (relatorioInadimplencia?.valor_total || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="dnapay-relatorios">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-purple-600" />
            Relatórios Financeiros
          </h1>
          <p className="text-gray-500">Análise detalhada das finanças</p>
        </div>
        
        <div className="flex gap-2">
          <Select value={String(ano)} onValueChange={(v) => setAno(Number(v))}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="2024">2024</SelectItem>
              <SelectItem value="2025">2025</SelectItem>
              <SelectItem value="2026">2026</SelectItem>
            </SelectContent>
          </Select>
          
          <Button variant="outline">
            <Download className="w-4 h-4 mr-2" />
            Exportar
          </Button>
        </div>
      </div>

      {/* Cards Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <DollarSign className="w-8 h-8" />
              <div>
                <p className="text-blue-100 text-sm">Previsão {ano}</p>
                <p className="text-2xl font-bold">{formatCurrency(totalPrevisao)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8" />
              <div>
                <p className="text-green-100 text-sm">Recebido {ano}</p>
                <p className="text-2xl font-bold">{formatCurrency(totalRecebido)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-500 to-red-700 text-white border-0">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <TrendingDown className="w-8 h-8" />
              <div>
                <p className="text-red-100 text-sm">Inadimplência Total</p>
                <p className="text-2xl font-bold">{formatCurrency(totalInadimplente)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="receita" className="space-y-4">
        <TabsList>
          <TabsTrigger value="receita">Receita Mensal</TabsTrigger>
          <TabsTrigger value="inadimplencia">Inadimplência</TabsTrigger>
          <TabsTrigger value="metodos">Métodos de Pagamento</TabsTrigger>
        </TabsList>

        {/* Receita Mensal */}
        <TabsContent value="receita">
          <Card>
            <CardHeader>
              <CardTitle>Receita Mensal {ano}</CardTitle>
              <CardDescription>Comparativo de previsão vs recebido por mês</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {receitaMensal.map((item) => {
                  const percentual = item.previsao > 0 ? (item.recebido / item.previsao * 100) : 0;
                  return (
                    <div key={item.mes} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium w-12">{meses[item.mes - 1]}</span>
                        <div className="flex-1 mx-4">
                          <Progress value={percentual} className="h-4" />
                        </div>
                        <div className="text-right min-w-[200px]">
                          <span className="text-green-600 font-medium">{formatCurrency(item.recebido)}</span>
                          <span className="text-gray-400 mx-1">/</span>
                          <span className="text-gray-500">{formatCurrency(item.previsao)}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inadimplência */}
        <TabsContent value="inadimplencia">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Taxa de Inadimplência Mensal</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {inadimplenciaMensal.map((item) => (
                    <div key={item.mes} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span>{meses[item.mes - 1]}</span>
                        <span className={item.taxa_inadimplencia > 10 ? 'text-red-600' : 'text-gray-600'}>
                          {item.taxa_inadimplencia.toFixed(1)}%
                        </span>
                      </div>
                      <Progress 
                        value={Math.min(item.taxa_inadimplencia, 100)} 
                        className={`h-2 ${item.taxa_inadimplencia > 10 ? '[&>div]:bg-red-500' : ''}`}
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-red-600" />
                  Detalhamento de Inadimplentes
                </CardTitle>
                <CardDescription>
                  {relatorioInadimplencia?.total_inadimplentes || 0} alunos com títulos vencidos
                </CardDescription>
              </CardHeader>
              <CardContent>
                {relatorioInadimplencia?.detalhamento?.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nenhum inadimplente</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {relatorioInadimplencia?.detalhamento?.slice(0, 10).map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-sm">{item.aluno_nome}</p>
                          <p className="text-xs text-gray-500">
                            {item.titulos_vencidos} título(s) vencido(s)
                          </p>
                        </div>
                        <p className="font-bold text-red-600">{formatCurrency(item.valor_total)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Métodos de Pagamento */}
        <TabsContent value="metodos">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-600" />
                Distribuição por Método de Pagamento
              </CardTitle>
              <CardDescription>Como os pagamentos estão sendo realizados</CardDescription>
            </CardHeader>
            <CardContent>
              {metodosPagamento.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum pagamento registrado</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {metodosPagamento.map((item) => {
                    const total = metodosPagamento.reduce((acc, m) => acc + m.total, 0);
                    const percentual = total > 0 ? (item.total / total * 100) : 0;
                    
                    const colorMap = {
                      pix: 'bg-green-100 border-green-300',
                      boleto: 'bg-blue-100 border-blue-300',
                      cartao_credito: 'bg-purple-100 border-purple-300',
                      cartao_debito: 'bg-orange-100 border-orange-300',
                      dinheiro: 'bg-yellow-100 border-yellow-300',
                      transferencia: 'bg-cyan-100 border-cyan-300',
                    };
                    
                    return (
                      <div 
                        key={item.metodo} 
                        className={`p-4 rounded-lg border-2 ${colorMap[item.metodo] || 'bg-gray-100 border-gray-300'}`}
                      >
                        <p className="text-sm text-gray-600 capitalize mb-1">
                          {item.metodo?.replace('_', ' ') || 'Outros'}
                        </p>
                        <p className="text-2xl font-bold">{formatCurrency(item.total)}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-gray-500">{item.quantidade} pagamentos</span>
                          <span className="text-sm font-medium">({percentual.toFixed(1)}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
